import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Building2, 
  Calendar, 
  CreditCard, 
  DollarSign, 
  FileText, 
  Loader2, 
  Plus, 
  RefreshCw, 
  TrendingUp, 
  Users,
  ArrowUpRight,
  LogOut,
  ChevronRight,
  ChevronDown,
  Sun,
  Moon,
  X,
  Edit,
  Layers,
  Briefcase,
  UserPlus,
  Coins,
  Trash2,
  ToggleLeft,
  ToggleRight,
  MoreVertical,
  Eye,
  EyeOff,
  Upload,
  Image as ImageIcon,
  Clock,
  IndianRupee,
  MapPin,
  Package,
  Star,
  Sparkles,
  UserCheck,
  CalendarOff,
  Ticket,
  Contact,
  Menu
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { OverviewTab } from '../components/admin/OverviewTab';
import { BookingsTab } from '../components/admin/BookingsTab';
import { VenuesTab } from '../components/admin/VenuesTab';
import { UsersTab } from '../components/admin/UsersTab';
import { OrganizationsTab } from '../components/admin/OrganizationsTab';
import { CreditsTab } from '../components/admin/CreditsTab';
import { PaymentsTab } from '../components/admin/PaymentsTab';
import { AnalyticsTab } from '../components/admin/AnalyticsTab';
import RoleAnalytics from '../components/analytics/RoleAnalytics';
import { ServiceRequestsTab } from '../components/admin/ServiceRequestsTab';
import { ContractsTab } from '../components/contracts/ContractsTab';
import { FeedbackTab } from '../components/feedback/FeedbackTab';
import { MemberFeedbackTab } from '../components/marketing/MemberFeedbackTab';
import { CreditsOverviewTab } from '../components/credits/CreditsOverviewTab';
import { DayPassOverviewTab } from '../components/day_passes/DayPassOverviewTab';
import { ApprovalsModule } from '../components/approvals/ApprovalsModule';
import { ApprovalsBell } from '../components/approvals/ApprovalsBell';
import { CouponsTab } from '../components/coupons/CouponsTab';
import { SaleTrackingTab } from '../components/admin/SaleTrackingTab';
import { HolidaysTab } from '../components/admin/HolidaysTab';
import ReportsTab from '../components/reports/ReportsTab';
import VirtualOfficeTab from '../components/virtual_office/VirtualOfficeTab';
import AvailabilityView from '../components/AvailabilityView';
import { ProfileModal } from '../components/ProfileModal';
import { EcosystemAdminTab } from '../components/ecosystem/EcosystemAdminTab';
import { InventoryTab } from '../components/inventory/InventoryTab';
import { VMSTab } from '../components/vms/VMSTab';
import { ClientDatabaseTab } from '../components/clients/ClientDatabaseTab';
import { Presentation } from 'lucide-react';
import { useStudioAccess } from '../components/proposal_studio/useStudioAccess';

const LOGO_URL = '/logo.webp';

// Roles an admin can grant a user as an *additional* role, switchable from
// their Profile alongside their primary role. Mirrors the primary Role
// <Select> options in the user modal below (minus whichever is selected as
// primary — a user can't grant themselves the same role twice).
const ADDITIONAL_ROLE_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'center_manager', label: 'Center Manager' },
  { value: 'cluster_manager', label: 'Cluster Manager' },
  { value: 'hub_manager', label: 'Hub Manager' },
  { value: 'sales', label: 'Sales' },
  { value: 'founders', label: 'Founders' },
  { value: 'finance_audit', label: 'Finance & Audit' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'marketing_ops', label: 'Marketing Ops' },
  { value: 'bd_transactions', label: 'BD Transactions' },
  { value: 'admin', label: 'Admin' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const studioAccess = useStudioAccess(api);
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [comprehensiveAnalytics, setComprehensiveAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [creditsByEntity, setCreditsByEntity] = useState({});
  const [venues, setVenues] = useState([]);
  const [bookables, setBookables] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [feedbackView, setFeedbackView] = useState('guest');
  const [centreAssignments, setCentreAssignments] = useState([]);
  const [centerManagers, setCenterManagers] = useState([]);
  const [adminTickets, setAdminTickets] = useState([]);
  
  // Modal states
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [showBookableModal, setShowBookableModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [expandedVenue, setExpandedVenue] = useState(null);
  const [savingVenue, setSavingVenue] = useState(false);
  const [savingBookable, setSavingBookable] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [savingCredit, setSavingCredit] = useState(false);
  const [selectedOrgForCredit, setSelectedOrgForCredit] = useState(null);
  const [selectedUserForCredit, setSelectedUserForCredit] = useState(null);
  
  // Edit mode states
  const [editMode, setEditMode] = useState(null); // 'user', 'org', 'venue', 'bookable'
  const [editingItem, setEditingItem] = useState(null);
  
  // Delete confirmation states
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [deleting, setDeleting] = useState(false);

  // Venue form
  const [venueForm, setVenueForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    region: '',
    area_locality: '',
    nearest_metro: '',
    metro_distance: '',
    walk_time: '',
    maps_link: '',
    phone: '',
    email: '',
    description: '',
    amenities: '',
    opening_time: '09:00',
    closing_time: '21:00',
    images: []
  });
  
  // Image upload states
  const [uploadingVenueImage, setUploadingVenueImage] = useState(false);
  const [uploadingBookableImage, setUploadingBookableImage] = useState(false);

  // Organization form
  const [orgForm, setOrgForm] = useState({
    name: '',
    gstin: '',
    billing_email: '',
    billing_address: '',
    phone: '',
    contact_person: ''
  });

  // User form
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    organization_id: '',
    designation: '',
    role: 'client',
    assigned_venue_ids: [],
    granted_roles: []
  });

  // Credit allocation form
  const [creditForm, setCreditForm] = useState({
    monthly_credits: 0,
    monthly_valid_till: '',
    additional_credits: 0,
    additional_valid_till: '',
    custom_rates: {
      enabled: false,
      central_bangalore: 2.0,
      koramangala: 2.0,
      south_bangalore: 1.5,
      electronic_city: 1.5,
      east_bangalore: 1.5
    }
  });

  // Bookable form
  const [bookableForm, setBookableForm] = useState({
    name: '',
    venue_id: '',
    bookable_type: 'meeting_room',
    capacity: 4,
    hourly_rate: 500,
    half_day_rate: 0,
    full_day_rate: 0,
    description: '',
    amenities: {
      wifi: true,
      whiteboard: true,
      tv_screen: true,
      ac: true,
      projector: false,
      video_conferencing: false,
      coffee_tea: false,
      water: true,
      power_outlets: true,
      natural_light: false
    },
    min_booking_hours: 1,
    max_booking_hours: 8,
    buffer_minutes: 15,
    gst_applicable: true,
    gst_rate: 18,
    images: []
  });

  useEffect(() => {
    fetchCoreData();
  }, [api]);

  // Core data — minimal set needed for tab navigation
  const fetchCoreData = async () => {
    try {
      const [venuesRes, bookablesRes] = await Promise.all([
        api.get('/venues/all'),
        api.get('/bookables/all'),
      ]);
      setVenues(venuesRes.data);
      setBookables(bookablesRes.data);
    } catch (error) {
      console.error('Error fetching core data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tab-specific data when tab changes
  useEffect(() => {
    if (loading) return;
    const fetchTabData = async () => {
      try {
        if (activeTab === 'overview' && !analytics) {
          const res = await api.get('/analytics/dashboard');
          setAnalytics(res.data);
        }
        if ((activeTab === 'bookings' || activeTab === 'overview') && bookings.length === 0) {
          const res = await api.get('/bookings');
          setBookings(res.data);
        }
        if ((activeTab === 'users' || activeTab === 'overview' || activeTab === 'bookings') && users.length === 0) {
          const res = await api.get('/users');
          setUsers(res.data);
        }
        if ((activeTab === 'organizations' || activeTab === 'overview' || activeTab === 'bookings') && organizations.length === 0) {
          const res = await api.get('/organizations');
          setOrganizations(res.data);
        }
        if (activeTab === 'venues' && centreAssignments.length === 0) {
          const [assignmentsRes, managersRes] = await Promise.all([
            api.get('/centre-assignments'),
            api.get('/centre-assignments/managers'),
          ]);
          setCentreAssignments(assignmentsRes.data);
          setCenterManagers(managersRes.data);
        }
      } catch (error) {
        console.error('Error fetching tab data:', error);
      }
    };
    fetchTabData();
  }, [activeTab, loading]);

  // Full refresh — used sparingly
  const fetchData = async () => {
    try {
      const [analyticsRes, bookingsRes, usersRes, venuesRes, bookablesRes, orgsRes, assignmentsRes, managersRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/bookings'),
        api.get('/users'),
        api.get('/venues/all'),
        api.get('/bookables/all'),
        api.get('/organizations'),
        api.get('/centre-assignments'),
        api.get('/centre-assignments/managers'),
      ]);
      setAnalytics(analyticsRes.data);
      setBookings(bookingsRes.data);
      setUsers(usersRes.data);
      setVenues(venuesRes.data);
      setBookables(bookablesRes.data);
      setOrganizations(orgsRes.data);
      setCentreAssignments(assignmentsRes.data);
      setCenterManagers(managersRes.data);

      // Fetch tickets
      try {
        const ticketsRes = await api.get('/tickets');
        setAdminTickets(ticketsRes.data);
      } catch (e) { /* non-critical */ }

      // Build credits map for inline columns on Organizations + Users tabs
      try {
        const cRes = await api.get('/credits/overview', { params: { days: 30, type: 'all' } });
        const map = {};
        (cRes.data?.items || []).forEach((it) => {
          if (it.entity_id) {
            map[it.entity_id] = {
              balance: it.balance,
              topup_balance: it.topup_balance,
              total_balance: it.total_balance,
              used_in_period: it.used_in_period,
              total_allocated: it.total_allocated,
            };
          }
        });
        setCreditsByEntity(map);
      } catch (e) { /* non-critical */ }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const refreshVenues = async () => {
    try {
      const [venuesRes, bookablesRes] = await Promise.all([
        api.get('/venues/all'),
        api.get('/bookables/all'),
      ]);
      setVenues(venuesRes.data);
      setBookables(bookablesRes.data);
    } catch (e) { console.error(e); }
  };

  const refreshUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (e) { console.error(e); }
  };

  const refreshBookings = async () => {
    try {
      const res = await api.get('/bookings');
      setBookings(res.data);
    } catch (e) { console.error(e); }
  };

  const refreshOrgs = async () => {
    try {
      const res = await api.get('/organizations');
      setOrganizations(res.data);
    } catch (e) { console.error(e); }
  };

  // Fetch comprehensive analytics when tab changes or period changes
  const fetchComprehensiveAnalytics = async (period = analyticsPeriod) => {
    if (user?.role !== 'admin') return;
    
    setAnalyticsLoading(true);
    try {
      let url = `/analytics/comprehensive?period=${period}`;
      if (period === 'custom' && customDateRange.start && customDateRange.end) {
        url += `&start_date=${customDateRange.start}&end_date=${customDateRange.end}`;
      }
      const res = await api.get(url);
      setComprehensiveAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching comprehensive analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics' && (user?.role === 'admin' || user?.role === 'hub_manager')) {
      fetchComprehensiveAnalytics();
    }
  }, [activeTab, analyticsPeriod]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const resetOrgForm = () => {
    setOrgForm({
      name: '',
      gstin: '',
      billing_email: '',
      billing_address: '',
      phone: '',
      contact_person: ''
    });
  };

  const resetUserForm = () => {
    setUserForm({
      name: '',
      email: '',
      phone: '',
      password: '',
      organization_id: '',
      designation: '',
      role: 'client',
      assigned_venue_ids: [],
      granted_roles: []
    });
  };

  const resetCreditForm = () => {
    setCreditForm({
      monthly_credits: 0,
      monthly_valid_till: '',
      additional_credits: 0,
      additional_valid_till: '',
      custom_rates: {
        enabled: false,
        central_bangalore: 2.0,
        koramangala: 2.0,
        south_bangalore: 1.5,
        electronic_city: 1.5,
        east_bangalore: 1.5
      }
    });
    setSelectedOrgForCredit(null);
    setSelectedUserForCredit(null);
  };

  const handleAddOrganization = async () => {
    if (!orgForm.name || !orgForm.billing_email) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingOrg(true);
    try {
      const payload = {
        name: orgForm.name,
        billing_email: orgForm.billing_email,
        gstin: orgForm.gstin || undefined,
        billing_address: orgForm.billing_address || undefined,
        phone: orgForm.phone || undefined,
        contact_person: orgForm.contact_person || undefined
      };
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await api.post('/organizations', payload);
      toast.success('Organization created successfully');
      setShowOrgModal(false);
      resetOrgForm();
      refreshOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleAddUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingUser(true);
    try {
      const payload = {
        name: userForm.name,
        email: userForm.email,
        phone: userForm.phone || undefined,
        password: userForm.password,
        organization_id: userForm.organization_id || undefined,
        designation: userForm.designation || undefined,
        role: userForm.role,
        assigned_venue_ids: ['landlord', 'cluster_manager'].includes(userForm.role) ? userForm.assigned_venue_ids : undefined,
        granted_roles: userForm.granted_roles.length ? Array.from(new Set([userForm.role, ...userForm.granted_roles])) : undefined
      };
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      await api.post('/users/create', payload);
      toast.success('User created successfully');
      setShowUserModal(false);
      resetUserForm();
      refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleAllocateCredits = async () => {
    const totalCredits = (creditForm.monthly_credits || 0) + (creditForm.additional_credits || 0);
    if (totalCredits <= 0) {
      toast.error('Please enter credits in at least one section');
      return;
    }

    if (!selectedOrgForCredit && !selectedUserForCredit) {
      toast.error('Please select an organization or user');
      return;
    }

    setSavingCredit(true);
    try {
      const targetKey = selectedOrgForCredit ? 'organization_id' : 'user_id';
      const targetVal = selectedOrgForCredit || selectedUserForCredit;

      // Build custom rates payload if org
      let customRatesPayload = undefined;
      if (selectedOrgForCredit && creditForm.custom_rates.enabled) {
        customRatesPayload = {
          "Central Bangalore": creditForm.custom_rates.central_bangalore,
          "Koramangala": creditForm.custom_rates.koramangala,
          "South Bangalore": creditForm.custom_rates.south_bangalore,
          "Electronic City": creditForm.custom_rates.electronic_city,
          "East Bangalore": creditForm.custom_rates.east_bangalore
        };
      }

      // Monthly credits allocation (subject to auto-renew + carry-forward)
      if (creditForm.monthly_credits > 0) {
        const payload = {
          [targetKey]: targetVal,
          credits: creditForm.monthly_credits,
          credit_type: 'monthly',
          monthly_allocation: creditForm.monthly_credits,
          auto_renew: creditForm.auto_renew !== undefined ? creditForm.auto_renew : true,
          carry_forward_percentage: creditForm.carry_forward_percentage || 0,
          renewal_cycle_months: creditForm.renewal_cycle_months || 1,
        };
        if (creditForm.monthly_valid_till) {
          payload.valid_until = `${creditForm.monthly_valid_till}-01T00:00:00Z`;
        }
        if (customRatesPayload) payload.custom_credit_rates = customRatesPayload;
        await api.post('/credits/allocate', payload);
      }

      // Additional one-time credits → TOP-UP bucket (never expires, never auto-renews)
      if (creditForm.additional_credits > 0) {
        const payload = {
          [targetKey]: targetVal,
          credits: creditForm.additional_credits,
          credit_type: 'topup',
        };
        if (customRatesPayload && creditForm.monthly_credits <= 0) payload.custom_credit_rates = customRatesPayload;
        await api.post('/credits/allocate', payload);
      }

      toast.success('Credits allocated successfully');
      setShowCreditModal(false);
      resetCreditForm();
      refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate credits');
    } finally {
      setSavingCredit(false);
    }
  };

  const openCreditModalForOrg = (orgId) => {
    setSelectedOrgForCredit(orgId);
    setSelectedUserForCredit(null);
    setShowCreditModal(true);
  };

  const openCreditModalForUser = (userId) => {
    setSelectedUserForCredit(userId);
    setSelectedOrgForCredit(null);
    setShowCreditModal(true);
  };

  const getOrganizationName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'N/A';
  };

  // Edit handlers
  const openEditUser = (u) => {
    setEditMode('user');
    setEditingItem(u);
    setUserForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      password: '',
      organization_id: u.organization_id || '',
      designation: u.designation || '',
      role: u.role || 'client',
      assigned_venue_ids: u.assigned_venue_ids || [],
      granted_roles: (u.granted_roles || []).filter(r => r !== (u.role || 'client'))
    });
    setShowUserModal(true);
  };

  const openEditOrg = (org) => {
    setEditMode('org');
    setEditingItem(org);
    setOrgForm({
      name: org.name || '',
      gstin: org.gstin || '',
      billing_email: org.billing_email || '',
      billing_address: org.billing_address || '',
      phone: org.phone || '',
      contact_person: org.contact_person || ''
    });
    setShowOrgModal(true);
  };

  const openEditVenue = (venue) => {
    setEditMode('venue');
    setEditingItem(venue);
    setVenueForm({
      name: venue.name || '',
      address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      pincode: venue.pincode || '',
      area_locality: venue.area_locality || '',
      nearest_metro: venue.nearest_metro || '',
      metro_distance: venue.metro_distance || '',
      maps_link: venue.maps_link || '',
      phone: venue.phone || '',
      email: venue.email || '',
      description: venue.description || '',
      amenities: venue.amenities?.join(', ') || '',
      opening_time: venue.opening_time || '09:00',
      closing_time: venue.closing_time || '21:00'
    });
    setShowVenueModal(true);
  };

  // Update handlers (for edit mode)
  const handleUpdateUser = async () => {
    if (!editingItem) return;
    setSavingUser(true);
    try {
      const payload = {
        name: userForm.name,
        email: userForm.email,
        phone: userForm.phone || undefined,
        organization_id: userForm.organization_id || null,
        designation: userForm.designation || undefined,
        role: userForm.role,
        assigned_venue_ids: ['landlord', 'cluster_manager'].includes(userForm.role) ? userForm.assigned_venue_ids : undefined,
        // Always sent (not conditionally omitted) so unchecking every
        // additional role actually clears granted_roles server-side.
        granted_roles: userForm.granted_roles.length ? Array.from(new Set([userForm.role, ...userForm.granted_roles])) : []
      };
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      await api.patch(`/users/${editingItem.id}`, payload);
      toast.success('User updated successfully');
      setShowUserModal(false);
      setEditMode(null);
      setEditingItem(null);
      resetUserForm();
      refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateOrg = async () => {
    if (!editingItem) return;
    setSavingOrg(true);
    try {
      const payload = {
        name: orgForm.name,
        billing_email: orgForm.billing_email,
        gstin: orgForm.gstin || undefined,
        billing_address: orgForm.billing_address || undefined,
        phone: orgForm.phone || undefined,
        contact_person: orgForm.contact_person || undefined
      };
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await api.patch(`/organizations/${editingItem.id}`, payload);
      toast.success('Organization updated successfully');
      setShowOrgModal(false);
      setEditMode(null);
      setEditingItem(null);
      resetOrgForm();
      refreshOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleUpdateVenue = async () => {
    if (!editingItem) return;
    setSavingVenue(true);
    try {
      const payload = {
        name: venueForm.name,
        address: venueForm.address,
        city: venueForm.city,
        state: venueForm.state || undefined,
        pincode: venueForm.pincode || undefined,
        region: venueForm.region || undefined,
        area_locality: venueForm.area_locality || undefined,
        nearest_metro: venueForm.nearest_metro || undefined,
        metro_distance: venueForm.metro_distance || undefined,
        maps_link: venueForm.maps_link || undefined,
        phone: venueForm.phone || undefined,
        email: venueForm.email || undefined,
        description: venueForm.description || undefined,
        amenities: venueForm.amenities ? venueForm.amenities.split(',').map(a => a.trim()).filter(a => a) : [],
        images: venueForm.images || [],
        thumbnails: venueForm.thumbnails || [],
        opening_time: venueForm.opening_time || '09:00',
        closing_time: venueForm.closing_time || '21:00'
      };
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      await api.patch(`/venues/${editingItem.id}`, payload);
      toast.success('Venue updated successfully');
      setShowVenueModal(false);
      setEditMode(null);
      setEditingItem(null);
      resetVenueForm();
      refreshVenues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update venue');
    } finally {
      setSavingVenue(false);
    }
  };

  // Delete handlers
  const confirmDelete = (type, item) => {
    setDeleteDialog({ open: true, type, item });
  };

  const handleDelete = async () => {
    const { type, item } = deleteDialog;
    if (!type || !item) return;
    
    setDeleting(true);
    try {
      let endpoint = '';
      switch (type) {
        case 'user': endpoint = `/users/${item.id}`; break;
        case 'org': endpoint = `/organizations/${item.id}`; break;
        case 'venue': endpoint = `/venues/${item.id}`; break;
        case 'bookable': endpoint = `/bookables/${item.id}`; break;
        default: return;
      }
      
      await api.delete(endpoint);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
      setDeleteDialog({ open: false, type: null, item: null });
      if (type === 'venue' || type === 'bookable') refreshVenues();
      else if (type === 'user') refreshUsers();
      else if (type === 'organization') refreshOrgs();
      else fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle active status
  const toggleUserActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const toggleOrgActive = async (org) => {
    try {
      await api.patch(`/organizations/${org.id}`, { is_active: !org.is_active });
      toast.success(`Organization ${org.is_active ? 'deactivated' : 'activated'}`);
      refreshOrgs();
    } catch (error) {
      toast.error('Failed to update organization status');
    }
  };

  const updateVenueStatus = async (venue, newStatus) => {
    try {
      await api.patch(`/venues/${venue.id}`, { venue_status: newStatus });
      toast.success(`Venue status updated to ${newStatus.replace('_', ' ')}`);
      refreshVenues();
    } catch (error) {
      toast.error('Failed to update venue status');
    }
  };

  const toggleBookableActive = async (bookable) => {
    try {
      await api.patch(`/bookables/${bookable.id}`, { is_active: !bookable.is_active });
      toast.success(`External availability ${bookable.is_active ? 'disabled' : 'enabled'}`);
      refreshVenues();
    } catch (error) {
      toast.error('Failed to update bookable status');
    }
  };

  const toggleBookableInternal = async (bookable) => {
    const current = bookable.is_active_internal !== false;
    try {
      await api.patch(`/bookables/${bookable.id}`, { is_active_internal: !current });
      toast.success(`Internal availability ${current ? 'disabled' : 'enabled'}`);
      refreshVenues();
    } catch (error) {
      toast.error('Failed to update bookable status');
    }
  };

  // Close modal and reset edit mode
  const closeUserModal = () => {
    setShowUserModal(false);
    setEditMode(null);
    setEditingItem(null);
    resetUserForm();
  };

  const closeOrgModal = () => {
    setShowOrgModal(false);
    setEditMode(null);
    setEditingItem(null);
    resetOrgForm();
  };

  const closeVenueModal = () => {
    setShowVenueModal(false);
    setEditMode(null);
    setEditingItem(null);
    resetVenueForm();
  };

  const resetVenueForm = () => {
    setVenueForm({
      name: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      region: '',
      area_locality: '',
      nearest_metro: '',
      metro_distance: '',
      walk_time: '',
      maps_link: '',
      phone: '',
      email: '',
      description: '',
      amenities: '',
      opening_time: '09:00',
      closing_time: '21:00',
      images: []
    });
  };

  const resetBookableForm = () => {
    setBookableForm({
      name: '',
      venue_id: '',
      bookable_type: 'meeting_room',
      capacity: 4,
      hourly_rate: 500,
      half_day_rate: 0,
      full_day_rate: 0,
      description: '',
      amenities: {
        wifi: true,
        whiteboard: true,
        tv_screen: true,
        ac: true,
        projector: false,
        video_conferencing: false,
        coffee_tea: false,
        water: true,
        power_outlets: true,
        natural_light: false
      },
      min_booking_hours: 1,
      max_booking_hours: 8,
      buffer_minutes: 15,
      gst_applicable: true,
      gst_rate: 18,
      images: []
    });
  };

  // Image upload handler
  const handleImageUpload = async (e, formType) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const setUploading = formType === 'venue' ? setUploadingVenueImage : setUploadingBookableImage;
    const setForm = formType === 'venue' ? setVenueForm : setBookableForm;
    
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await api.post('/upload/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.urls && response.data.urls.length > 0) {
        setForm(prev => ({
          ...prev,
          images: [...(prev.images || []), ...response.data.urls],
          thumbnails: [...(prev.thumbnails || []), ...(response.data.thumbnails || [])]
        }));
        toast.success(`${response.data.urls.length} image(s) uploaded`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (formType, index) => {
    const setForm = formType === 'venue' ? setVenueForm : setBookableForm;
    setForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddVenue = async () => {
    if (!venueForm.name || !venueForm.address || !venueForm.city) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingVenue(true);
    try {
      const payload = {
        name: venueForm.name,
        address: venueForm.address,
        city: venueForm.city,
        state: venueForm.state || undefined,
        pincode: venueForm.pincode || undefined,
        region: venueForm.region || undefined,
        area_locality: venueForm.area_locality || undefined,
        nearest_metro: venueForm.nearest_metro || undefined,
        metro_distance: venueForm.metro_distance || undefined,
        walk_time: venueForm.walk_time || undefined,
        maps_link: venueForm.maps_link || undefined,
        phone: venueForm.phone || undefined,
        email: venueForm.email || undefined,
        description: venueForm.description || undefined,
        amenities: venueForm.amenities ? venueForm.amenities.split(',').map(a => a.trim()).filter(a => a) : [],
        images: venueForm.images || [],
        thumbnails: venueForm.thumbnails || [],
        opening_time: venueForm.opening_time || '09:00',
        closing_time: venueForm.closing_time || '21:00'
      };
      
      // Remove undefined values
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await api.post('/venues', payload);
      toast.success('Venue added successfully');
      setShowVenueModal(false);
      resetVenueForm();
      refreshVenues();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      const displayMsg = typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg;
      toast.error(displayMsg || 'Failed to add venue');
    } finally {
      setSavingVenue(false);
    }
  };

  const handleAddBookable = async () => {
    if (!bookableForm.name || !bookableForm.venue_id) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingBookable(true);
    try {
      // Calculate half day (4 hrs with 10% discount) and full day (8 hrs with 25% discount)
      const halfDayRate = Math.round(bookableForm.hourly_rate * 4 * 0.9);
      const fullDayRate = Math.round(bookableForm.hourly_rate * 8 * 0.75);
      
      // Convert amenities object to array of selected amenities
      const amenitiesArray = Object.entries(bookableForm.amenities)
        .filter(([_, selected]) => selected)
        .map(([key, _]) => key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
      
      const payload = {
        name: bookableForm.name,
        venue_id: bookableForm.venue_id,
        bookable_type: bookableForm.bookable_type,
        capacity: bookableForm.capacity,
        hourly_rate: bookableForm.hourly_rate,
        half_day_rate: halfDayRate,
        full_day_rate: fullDayRate,
        description: bookableForm.description || undefined,
        amenities: amenitiesArray,
        images: bookableForm.images || [],
        min_booking_hours: bookableForm.min_booking_hours || 1,
        max_booking_hours: bookableForm.max_booking_hours || 8,
        buffer_minutes: bookableForm.buffer_minutes || 15,
        gst_applicable: bookableForm.gst_applicable !== false,
        gst_rate: bookableForm.gst_rate || 18
      };
      
      // Remove undefined values
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await api.post('/bookables', payload);
      toast.success('Bookable added successfully');
      setShowBookableModal(false);
      resetBookableForm();
      refreshVenues();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      const displayMsg = typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg;
      toast.error(displayMsg || 'Failed to add bookable');
    } finally {
      setSavingBookable(false);
    }
  };

  const openAddBookableModal = (venueId) => {
    setBookableForm(prev => ({ ...prev, venue_id: venueId }));
    setShowBookableModal(true);
  };

  const getBookablesForVenue = (venueId) => {
    return bookables.filter(b => b.venue_id === venueId);
  };

  const groupBookablesByType = (venueBookables) => {
    const grouped = {};
    venueBookables.forEach(b => {
      if (!grouped[b.bookable_type]) {
        grouped[b.bookable_type] = [];
      }
      grouped[b.bookable_type].push(b);
    });
    return grouped;
  };

  const recentBookings = bookings.slice(0, 10);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-500 border-red-500/30';
      case 'completed': return 'bg-primary/20 text-primary border-primary/30';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const bookableTypeLabels = {
    meeting_room: 'Meeting Room',
    conference_room: 'Conference Room',
    day_pass: 'Day Pass',
    event_space: 'Event Space',
    virtual_office: 'Virtual Office'
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'availability', label: 'Availability', icon: Eye },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'venues', label: 'Venues', icon: Building2 },
    { id: 'organizations', label: 'Organizations', icon: Briefcase },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'credits', label: 'Credits', icon: Coins },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'tickets', label: 'Tickets', icon: Star },
    { id: 'contracts', label: 'Contracts', icon: FileText },
    { id: 'feedback', label: 'Feedback', icon: Star },
    { id: 'approvals', label: 'Approvals', icon: FileText },
    { id: 'coupons', label: 'Coupons', icon: Star },
    { id: 'sale_tracking', label: 'Diwali Sale Tracking', icon: TrendingUp },
    { id: 'holidays', label: 'Holidays', icon: CalendarOff },
    { id: 'virtual_office', label: 'Virtual Office', icon: Briefcase },
    { id: 'ecosystem', label: 'Ecosystem', icon: Sparkles },
    { id: 'inventory', label: 'Inventory', icon: Building2 },
    { id: 'credits_overview', label: 'Credits Overview', icon: Star },
    { id: 'day_passes_overview', label: 'Bulk Day Passes', icon: Ticket },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'service_requests', label: 'Service Requests', icon: Package },
    { id: 'vms', label: 'VMS', icon: UserCheck },
    { id: 'client_database', label: 'Client Database', icon: Contact },
  ];

  // Theme-aware colors
  const sidebarBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const sidebarBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const cardBg = isDark ? 'bg-[#13161a]' : 'bg-white';
  const cardBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const innerCardBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const hoverBg = isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]';
  const inputBg = isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-[#faf8f3] border-[#e5dfd0]';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`hidden lg:flex w-64 flex-col fixed inset-y-0 z-50 ${sidebarBg} border-r ${sidebarBorder}`}>
          <div className={`px-4 py-2.5 border-b ${sidebarBorder}`}>
            <Link to="/admin" className="flex items-center justify-center h-8">
              <img src={LOGO_URL} alt="Clayworks" className="h-7 w-auto" />
            </Link>
          </div>
          
          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
            {sidebarItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id 
                    ? 'bg-primary text-primary-foreground' 
                    : `text-muted-foreground hover:text-foreground ${hoverBg}`
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
            {studioAccess && (
              <Link
                to="/proposal-studio"
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all text-muted-foreground hover:text-foreground ${hoverBg}`}
                data-testid="nav-proposal-studio"
              >
                <Presentation className="w-4 h-4" />
                Proposal Studio
              </Link>
            )}
          </nav>

          <div className={`px-3 py-2 border-t ${sidebarBorder} space-y-1`}>
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground ${hoverBg}`}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowProfileModal(true)}
                className={`flex items-center gap-2 flex-1 min-w-0 p-2 rounded-xl ${cardBg} hover:ring-1 hover:ring-primary/30 transition-all cursor-pointer text-left`}
                data-testid="sidebar-profile-btn"
              >
                {user?.photo_url ? (
                  <img src={user.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <Users className="w-3.5 h-3.5 text-primary" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </button>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-muted-foreground hover:text-destructive shrink-0 px-2"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile nav drawer */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className={`w-64 p-0 flex flex-col ${sidebarBg} ${sidebarBorder}`}>
            <SheetHeader className={`px-4 py-2.5 border-b ${sidebarBorder} space-y-0`}>
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <Link to="/admin" className="flex items-center justify-center h-8" onClick={() => setMobileNavOpen(false)}>
                <img src={LOGO_URL} alt="Clayworks" className="h-7 w-auto" />
              </Link>
            </SheetHeader>

            <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
              {sidebarItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setMobileNavOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    activeTab === item.id
                      ? 'bg-primary text-primary-foreground'
                      : `text-muted-foreground hover:text-foreground ${hoverBg}`
                  }`}
                  data-testid={`mobile-nav-${item.id}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
              {studioAccess && (
                <Link
                  to="/proposal-studio"
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all text-muted-foreground hover:text-foreground ${hoverBg}`}
                  data-testid="mobile-nav-proposal-studio"
                >
                  <Presentation className="w-4 h-4" />
                  Proposal Studio
                </Link>
              )}
            </nav>

            <div className={`px-3 py-2 border-t ${sidebarBorder} space-y-1`}>
              <button
                onClick={toggleTheme}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground ${hoverBg}`}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={() => { setMobileNavOpen(false); handleLogout(); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-destructive ${hoverBg}`}
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </SheetContent>
        </Sheet>

        <ProfileModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={user}
          api={api}
          onProfileUpdate={() => window.location.reload()}
        />

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
          <div className="p-6 lg:p-8 min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMobileNavOpen(true)}
                  className={`lg:hidden shrink-0 p-2 rounded-xl ${cardBorder} border ${hoverBg}`}
                  aria-label="Open menu"
                  data-testid="mobile-nav-open-btn"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                    {activeTab === 'overview' ? 'Dashboard' : sidebarItems.find(i => i.id === activeTab)?.label}
                  </h1>
                  <p className="text-muted-foreground mt-1">Welcome back, {user?.name}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <ApprovalsBell api={api} user={user} onClick={() => setActiveTab('approvals')} />
                <Button variant="outline" size="sm" className={`${cardBorder} ${hoverBg}`} onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                {(user?.role === 'admin' || user?.role === 'hub_manager') && (
                  <Button 
                    className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25" 
                    size="sm" 
                    onClick={() => setShowVenueModal(true)}
                    data-testid="add-venue-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Venue
                  </Button>
                )}
              </div>
            </div>

            {/* Tab Content - Rendered via extracted components */}
            {activeTab === 'overview' && (
              <OverviewTab
                analytics={analytics}
                bookings={bookings}
                venues={venues}
                theme={{ cardBg, cardBorder, innerCardBg, hoverBg }}
                onTabChange={setActiveTab}
              />
            )}

            {activeTab === 'availability' && (
              <AvailabilityView venues={venues} />
            )}

            {activeTab === 'bookings' && (
              <BookingsTab
                bookings={bookings}
                venues={venues}
                bookables={bookables}
                users={users}
                user={user}
                api={api}
                theme={{ cardBg, cardBorder, hoverBg }}
                onRefresh={fetchData}
              />
            )}

            {activeTab === 'venues' && (
              <VenuesTab
                venues={venues}
                bookables={bookables}
                user={user}
                expandedVenue={expandedVenue}
                theme={{ cardBg, cardBorder, innerCardBg }}
                onExpandVenue={setExpandedVenue}
                onAddVenue={() => setShowVenueModal(true)}
                onEditVenue={openEditVenue}
                onAddBookable={openAddBookableModal}
                onUpdateVenueStatus={updateVenueStatus}
                onToggleBookableActive={toggleBookableActive}
                onToggleBookableInternal={toggleBookableInternal}
                onDeleteVenue={(v) => confirmDelete('venue', v)}
                onDeleteBookable={(b) => confirmDelete('bookable', b)}
                centreAssignments={centreAssignments}
                centerManagers={centerManagers}
                api={api}
                onRefresh={fetchData}
              />
            )}

            {activeTab === 'users' && (
              <UsersTab
                users={users}
                currentUser={user}
                organizations={organizations}
                theme={{ cardBg, cardBorder, hoverBg }}
                api={api}
                creditsByEntity={creditsByEntity}
                onAddUser={() => { setEditMode(null); setEditingItem(null); setShowUserModal(true); }}
                onEditUser={openEditUser}
                onToggleActive={toggleUserActive}
                onDelete={(u) => confirmDelete('user', u)}
                onAddCredits={openCreditModalForUser}
                onRefresh={fetchData}
              />
            )}

            {activeTab === 'organizations' && (
              <OrganizationsTab
                organizations={organizations}
                users={users}
                theme={{ cardBg, cardBorder, innerCardBg }}
                creditsByEntity={creditsByEntity}
                onAddOrg={() => setShowOrgModal(true)}
                onEditOrg={openEditOrg}
                onToggleActive={toggleOrgActive}
                onDelete={(org) => confirmDelete('org', org)}
                onAddCredits={openCreditModalForOrg}
                onAddUserToOrg={(orgId) => {
                  setEditMode(null);
                  setUserForm(prev => ({ ...prev, organization_id: orgId }));
                  setShowUserModal(true);
                }}
              />
            )}

            {activeTab === 'credits' && (
              <CreditsTab
                organizations={organizations}
                users={users}
                theme={{ cardBg, cardBorder, innerCardBg }}
                onAddOrgCredits={openCreditModalForOrg}
                onAddUserCredits={openCreditModalForUser}
              />
            )}

            {activeTab === 'payments' && (
              <PaymentsTab
                api={api}
                isDark={isDark}
                theme={{ cardBg, cardBorder, innerCardBg, inputBg }}
              />
            )}

            {activeTab === 'analytics' && (user?.role === 'admin' || user?.role === 'hub_manager') && (
              <RoleAnalytics role="admin" />
            )}

            {activeTab === 'invoices' && (
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Invoice Management</h3>
                  <p className="text-muted-foreground">Invoice generation and tracking coming soon</p>
                </CardContent>
              </Card>
            )}

            {activeTab === 'tickets' && (
              <div className="space-y-6" data-testid="admin-tickets-tab">
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading">Client Tickets</CardTitle>
                    <Badge variant="outline" className={cardBorder}>{adminTickets.length} total</Badge>
                  </CardHeader>
                  <CardContent>
                    {adminTickets.length === 0 ? (
                      <p className="text-center py-8 text-muted-foreground">No tickets raised yet</p>
                    ) : (
                      <div className="space-y-3">
                        {adminTickets.map(ticket => (
                          <div key={ticket.id} className={`p-4 rounded-lg border ${cardBorder} space-y-2`} data-testid={`admin-ticket-${ticket.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{ticket.user_name} <span className="text-muted-foreground font-normal">({ticket.user_email})</span></p>
                                <p className="text-sm mt-1">{ticket.description}</p>
                                {ticket.venue_name && <p className="text-xs text-muted-foreground mt-1">Venue: {ticket.venue_name}</p>}
                                <p className="text-[10px] text-muted-foreground mt-1">{new Date(ticket.created_at).toLocaleString()}</p>
                              </div>
                              <Select
                                value={ticket.status}
                                onValueChange={async (newStatus) => {
                                  try {
                                    await api.patch(`/tickets/${ticket.id}`, { status: newStatus });
                                    toast.success('Ticket updated');
                                    const res = await api.get('/tickets');
                                    setAdminTickets(res.data);
                                  } catch { toast.error('Failed to update'); }
                                }}
                              >
                                <SelectTrigger className="w-[130px] h-8 text-xs shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="open">Open</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {ticket.photo_url && (
                              <img
                                src={ticket.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ticket.photo_url}` : ticket.photo_url}
                                alt="Issue"
                                className="w-40 h-40 rounded-lg object-cover border border-border cursor-pointer"
                                onClick={() => window.open(ticket.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ticket.photo_url}` : ticket.photo_url, '_blank')}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'contracts' && (
              <ContractsTab api={api} venues={venues} userRole={user?.role} />
            )}

            {activeTab === 'feedback' && (
              <div className="space-y-6">
                <div className="inline-flex rounded-lg border p-1 gap-1" data-testid="admin-feedback-view-switch">
                  {[['guest', 'Guest Feedback'], ['member', 'Member Feedback (CSAT/NPS)']].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setFeedbackView(key)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        feedbackView === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`admin-feedback-view-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {feedbackView === 'member' ? (
                  <MemberFeedbackTab api={api} />
                ) : (
                  <FeedbackTab api={api} venues={venues} isAdminOrHM={true} />
                )}
              </div>
            )}

            {activeTab === 'inventory' && (
              <InventoryTab api={api} user={user} venues={venues} />
            )}

            {activeTab === 'approvals' && (
              <ApprovalsModule api={api} user={user} />
            )}

            {activeTab === 'coupons' && (
              <CouponsTab api={api} venues={venues} />
            )}

            {activeTab === 'sale_tracking' && (
              <SaleTrackingTab api={api} venues={venues} />
            )}

            {activeTab === 'holidays' && (
              <HolidaysTab api={api} />
            )}

            {activeTab === 'virtual_office' && (
              <VirtualOfficeTab api={api} theme={{ cardBg, cardBorder, innerCardBg, inputBg }} isAdmin={true} />
            )}

            {activeTab === 'ecosystem' && (
              <EcosystemAdminTab api={api} />
            )}

            {activeTab === 'credits_overview' && (
              <CreditsOverviewTab api={api} isAdmin={true} />
            )}

            {activeTab === 'day_passes_overview' && (
              <DayPassOverviewTab api={api} isAdmin={true} canDelete={user?.role === 'admin'} venues={venues} />
            )}

            {activeTab === 'reports' && (
              <ReportsTab api={api} cardBg={cardBg} cardBorder={cardBorder} innerCardBg={innerCardBg} inputBg={inputBg} />
            )}

            {activeTab === 'service_requests' && (
              <ServiceRequestsTab
                api={api}
                theme={{ cardBg, cardBorder, innerCardBg }}
              />
            )}

            {activeTab === 'vms' && (
              <VMSTab api={api} venues={venues} canDelete={user?.role === 'admin'} />
            )}

            {activeTab === 'client_database' && (
              <ClientDatabaseTab user={user} />
            )}
          </div>
        </main>
      </div>

      {/* Add/Edit Venue Modal */}
      <Dialog open={showVenueModal} onOpenChange={closeVenueModal}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editMode === 'venue' ? 'Edit Venue' : 'Add New Venue'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Venue Name *</Label>
                <Input 
                  placeholder="e.g., Clayworks Koramangala"
                  value={venueForm.name}
                  onChange={(e) => setVenueForm({...venueForm, name: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>City *</Label>
                <Input 
                  placeholder="e.g., Bangalore"
                  value={venueForm.city}
                  onChange={(e) => setVenueForm({...venueForm, city: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address *</Label>
              <Input
                placeholder="Full street address"
                value={venueForm.address}
                onChange={(e) => setVenueForm({...venueForm, address: e.target.value})}
                className={inputBg}
              />
            </div>
            <div className="space-y-2">
              <Label>Google Maps Link</Label>
              <Input
                placeholder="https://maps.app.goo.gl/... (optional — auto-generated from address if left blank)"
                value={venueForm.maps_link}
                onChange={(e) => setVenueForm({...venueForm, maps_link: e.target.value})}
                className={inputBg}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>State</Label>
                <Input 
                  placeholder="e.g., Karnataka"
                  value={venueForm.state}
                  onChange={(e) => setVenueForm({...venueForm, state: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Pincode</Label>
                <Input 
                  placeholder="e.g., 560001"
                  value={venueForm.pincode}
                  onChange={(e) => setVenueForm({...venueForm, pincode: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Area / Locality</Label>
              <Input 
                placeholder="e.g., HSR Layout, Indiranagar"
                value={venueForm.area_locality}
                onChange={(e) => setVenueForm({...venueForm, area_locality: e.target.value})}
                className={inputBg}
              />
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <select
                value={venueForm.region}
                onChange={(e) => setVenueForm({...venueForm, region: e.target.value})}
                className={`w-full h-10 px-3 rounded-md border ${isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}
              >
                <option value="">Select Region</option>
                <option value="South Bangalore">South Bangalore</option>
                <option value="Central Bangalore">Central Bangalore</option>
                <option value="East Bangalore">East Bangalore</option>
                <option value="North Bangalore">North Bangalore</option>
                <option value="Koramangala">Koramangala</option>
                <option value="Electronic City">Electronic City</option>
                <option value="Whitefield">Whitefield</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Nearest Metro Station</Label>
                <Input 
                  placeholder="e.g., Indiranagar Metro"
                  value={venueForm.nearest_metro}
                  onChange={(e) => setVenueForm({...venueForm, nearest_metro: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Distance to Metro</Label>
                <Input 
                  placeholder="e.g., 500m, 1.2 km"
                  value={venueForm.metro_distance}
                  onChange={(e) => setVenueForm({...venueForm, metro_distance: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Walk Time to Metro</Label>
                <Input 
                  placeholder="e.g., ~15 min"
                  value={venueForm.walk_time}
                  onChange={(e) => setVenueForm({...venueForm, walk_time: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  placeholder="+91 80 4567 8901"
                  value={venueForm.phone}
                  onChange={(e) => setVenueForm({...venueForm, phone: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  placeholder="venue@clayworks.in"
                  value={venueForm.email}
                  onChange={(e) => setVenueForm({...venueForm, email: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Opening Time</Label>
                <Input 
                  type="time"
                  value={venueForm.opening_time}
                  onChange={(e) => setVenueForm({...venueForm, opening_time: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Closing Time</Label>
                <Input 
                  type="time"
                  value={venueForm.closing_time}
                  onChange={(e) => setVenueForm({...venueForm, closing_time: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                placeholder="Brief description of the venue..."
                value={venueForm.description}
                onChange={(e) => setVenueForm({...venueForm, description: e.target.value})}
                className={inputBg}
              />
            </div>
            <div className="space-y-2">
              <Label>Amenities (comma-separated)</Label>
              <Input 
                placeholder="WiFi, Parking, Cafeteria, 24/7 Access"
                value={venueForm.amenities}
                onChange={(e) => setVenueForm({...venueForm, amenities: e.target.value})}
                className={inputBg}
              />
            </div>
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label>Venue Images</Label>
              <div className={`p-4 rounded-lg border-2 border-dashed ${cardBorder} ${isDark ? 'bg-[#0a0b0d]' : 'bg-gray-50'}`}>
                <input
                  type="file"
                  id="venue-image-upload"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'venue')}
                  className="hidden"
                />
                <label 
                  htmlFor="venue-image-upload"
                  className="flex flex-col items-center justify-center cursor-pointer py-4"
                >
                  {uploadingVenueImage ? (
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload images</span>
                      <span className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP (max 5MB each)</span>
                    </>
                  )}
                </label>
              </div>
              
              {/* Image Previews */}
              {venueForm.images && venueForm.images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {venueForm.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                        alt={`Venue ${idx + 1}`}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage('venue', idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeVenueModal} className={cardBorder}>
              Cancel
            </Button>
            <Button 
              onClick={editMode === 'venue' ? handleUpdateVenue : handleAddVenue} 
              disabled={savingVenue} 
              className="bg-primary hover:bg-primary/90"
            >
              {savingVenue ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (editMode === 'venue' ? <Edit className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
              {editMode === 'venue' ? 'Update Venue' : 'Add Venue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Bookable Modal */}
      <Dialog open={showBookableModal} onOpenChange={setShowBookableModal}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New Bookable</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bookable Name *</Label>
                <Input 
                  placeholder="e.g., 4-Seater Meeting Room A"
                  value={bookableForm.name}
                  onChange={(e) => setBookableForm({...bookableForm, name: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Bookable Type *</Label>
                <Select 
                  value={bookableForm.bookable_type} 
                  onValueChange={(v) => setBookableForm({...bookableForm, bookable_type: v})}
                >
                  <SelectTrigger className={inputBg}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meeting_room">Meeting Room</SelectItem>
                    <SelectItem value="conference_room">Conference Room</SelectItem>
                    <SelectItem value="day_pass">Day Pass</SelectItem>
                    <SelectItem value="event_space">Event Space</SelectItem>
                    <SelectItem value="virtual_office">Virtual Office</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Capacity *</Label>
                <Input 
                  type="number"
                  min={1}
                  value={bookableForm.capacity}
                  onChange={(e) => setBookableForm({...bookableForm, capacity: parseInt(e.target.value) || 1})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate (₹) *</Label>
                <Input 
                  type="number"
                  min={0}
                  value={bookableForm.hourly_rate}
                  onChange={(e) => setBookableForm({...bookableForm, hourly_rate: parseFloat(e.target.value) || 0})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Booking Discounts</Label>
              <div className={`p-3 rounded-lg ${inputBg} text-sm space-y-1`}>
                <div className="flex justify-between">
                  <span>1-4 hours</span>
                  <span className="text-muted-foreground">No discount</span>
                </div>
                <div className="flex justify-between">
                  <span>4-6 hours</span>
                  <span className="text-green-500">10% discount</span>
                </div>
                <div className="flex justify-between">
                  <span>6-8 hours</span>
                  <span className="text-green-500">25% discount</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Booking Hours</Label>
                <Input 
                  type="number"
                  min={1}
                  value={bookableForm.min_booking_hours}
                  onChange={(e) => setBookableForm({...bookableForm, min_booking_hours: parseInt(e.target.value) || 1})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Booking Hours</Label>
                <Input 
                  type="number"
                  min={1}
                  value={bookableForm.max_booking_hours}
                  onChange={(e) => setBookableForm({...bookableForm, max_booking_hours: parseInt(e.target.value) || 8})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>GST Rate (%)</Label>
              <Input 
                type="number"
                min={0}
                max={100}
                value={bookableForm.gst_rate}
                onChange={(e) => setBookableForm({...bookableForm, gst_rate: parseFloat(e.target.value) || 18})}
                className={`${inputBg} w-32`}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                placeholder="Brief description of this bookable space..."
                value={bookableForm.description}
                onChange={(e) => setBookableForm({...bookableForm, description: e.target.value})}
                className={inputBg}
              />
            </div>
            <div className="space-y-3">
              <Label>Amenities</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'wifi', label: 'WiFi' },
                  { key: 'whiteboard', label: 'Whiteboard' },
                  { key: 'tv_screen', label: 'TV/Screen' },
                  { key: 'ac', label: 'Air Conditioning' },
                  { key: 'projector', label: 'Projector' },
                  { key: 'video_conferencing', label: 'Video Conferencing' },
                  { key: 'coffee_tea', label: 'Coffee/Tea' },
                  { key: 'water', label: 'Water' },
                  { key: 'power_outlets', label: 'Power Outlets' },
                  { key: 'natural_light', label: 'Natural Light' }
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bookableForm.amenities[key] || false}
                      onChange={(e) => setBookableForm({
                        ...bookableForm,
                        amenities: { ...bookableForm.amenities, [key]: e.target.checked }
                      })}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Image Upload Section for Bookable */}
            <div className="space-y-2">
              <Label>Bookable Images</Label>
              <div className={`p-4 rounded-lg border-2 border-dashed ${cardBorder} ${isDark ? 'bg-[#0a0b0d]' : 'bg-gray-50'}`}>
                <input
                  type="file"
                  id="bookable-image-upload"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'bookable')}
                  className="hidden"
                />
                <label 
                  htmlFor="bookable-image-upload"
                  className="flex flex-col items-center justify-center cursor-pointer py-4"
                >
                  {uploadingBookableImage ? (
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload images</span>
                      <span className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP (max 5MB each)</span>
                    </>
                  )}
                </label>
              </div>
              
              {/* Image Previews */}
              {bookableForm.images && bookableForm.images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {bookableForm.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                        alt={`Bookable ${idx + 1}`}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage('bookable', idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookableModal(false)} className={cardBorder}>
              Cancel
            </Button>
            <Button onClick={handleAddBookable} disabled={savingBookable} className="bg-primary hover:bg-primary/90">
              {savingBookable ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Bookable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Organization Modal */}
      <Dialog open={showOrgModal} onOpenChange={closeOrgModal}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editMode === 'org' ? 'Edit Organization' : 'Add New Organization'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Organization Name *</Label>
              <Input 
                placeholder="e.g., Acme Corporation"
                value={orgForm.name}
                onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
                className={inputBg}
                data-testid="org-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Billing Email *</Label>
                <Input 
                  type="email"
                  placeholder="billing@company.com"
                  value={orgForm.billing_email}
                  onChange={(e) => setOrgForm({...orgForm, billing_email: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  placeholder="+91 80 4567 8901"
                  value={orgForm.phone}
                  onChange={(e) => setOrgForm({...orgForm, phone: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>GSTIN</Label>
                <Input 
                  placeholder="e.g., 29ABCDE1234F1Z5"
                  value={orgForm.gstin}
                  onChange={(e) => setOrgForm({...orgForm, gstin: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input 
                  placeholder="Primary contact name"
                  value={orgForm.contact_person}
                  onChange={(e) => setOrgForm({...orgForm, contact_person: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Billing Address</Label>
              <Textarea 
                placeholder="Full billing address..."
                value={orgForm.billing_address}
                onChange={(e) => setOrgForm({...orgForm, billing_address: e.target.value})}
                className={inputBg}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeOrgModal} className={cardBorder}>
              Cancel
            </Button>
            <Button 
              onClick={editMode === 'org' ? handleUpdateOrg : handleAddOrganization} 
              disabled={savingOrg} 
              className="bg-primary hover:bg-primary/90"
            >
              {savingOrg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (editMode === 'org' ? <Edit className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
              {editMode === 'org' ? 'Update Organization' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit User Modal */}
      <Dialog open={showUserModal} onOpenChange={closeUserModal}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editMode === 'user' ? 'Edit User' : 'Add New User'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input 
                  placeholder="John Doe"
                  value={userForm.name}
                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                  className={inputBg}
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input 
                  type="email"
                  placeholder="john@company.com"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  placeholder="+91 98765 43210"
                  value={userForm.phone}
                  onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                  className={inputBg}
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input 
                  type="password"
                  placeholder="Create a password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organization / Company</Label>
                <Select 
                  value={userForm.organization_id} 
                  onValueChange={(val) => setUserForm({...userForm, organization_id: val === 'none' ? '' : val})}
                >
                  <SelectTrigger className={inputBg}>
                    <SelectValue placeholder="Select organization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Individual (No Organization)</SelectItem>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {userForm.organization_id ? 'B2B User' : 'B2C User (Individual)'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input 
                  placeholder="e.g., Marketing Manager"
                  value={userForm.designation}
                  onChange={(e) => setUserForm({...userForm, designation: e.target.value})}
                  className={inputBg}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={userForm.role} onValueChange={(val) => setUserForm({...userForm, role: val})}>
                  <SelectTrigger className={inputBg}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="center_manager">Center Manager</SelectItem>
                    <SelectItem value="cluster_manager">Cluster Manager</SelectItem>
                    <SelectItem value="hub_manager">Hub Manager</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="founders">Founders</SelectItem>
                    <SelectItem value="finance_audit">Finance & Audit</SelectItem>
                    {user?.role === 'admin' && <SelectItem value="landlord">Landlord</SelectItem>}
                    {user?.role === 'admin' && <SelectItem value="marketing_ops">Marketing Ops</SelectItem>}
                    {user?.role === 'admin' && <SelectItem value="bd_transactions">BD Transactions</SelectItem>}
                    {user?.role === 'admin' && <SelectItem value="admin">Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {user?.role === 'admin' && (
              <div className="space-y-2">
                <Label>Additional Roles</Label>
                <p className="text-xs text-muted-foreground">
                  Grant extra role(s) this user can switch into from their Profile, on top of the primary role above.
                </p>
                <div className={`rounded-md border ${cardBorder} p-3 max-h-48 overflow-y-auto space-y-2`}>
                  {ADDITIONAL_ROLE_OPTIONS.filter(r => r.value !== userForm.role).map(r => (
                    <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={userForm.granted_roles.includes(r.value)}
                        onCheckedChange={(checked) => {
                          setUserForm(prev => ({
                            ...prev,
                            granted_roles: checked
                              ? [...prev.granted_roles, r.value]
                              : prev.granted_roles.filter(v => v !== r.value)
                          }));
                        }}
                        data-testid={`additional-role-checkbox-${r.value}`}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(userForm.role === 'landlord' || userForm.role === 'cluster_manager') && (
              <div className="space-y-2">
                <Label>{userForm.role === 'cluster_manager' ? 'Assigned Centre(s)' : 'Assigned Building(s)'}</Label>
                <div className={`rounded-md border ${cardBorder} p-3 max-h-48 overflow-y-auto space-y-2`}>
                  {venues.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No {userForm.role === 'cluster_manager' ? 'centres' : 'buildings'} available</p>
                  ) : venues.map(v => (
                    <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={userForm.assigned_venue_ids.includes(v.id)}
                        onCheckedChange={(checked) => {
                          setUserForm(prev => ({
                            ...prev,
                            assigned_venue_ids: checked
                              ? [...prev.assigned_venue_ids, v.id]
                              : prev.assigned_venue_ids.filter(id => id !== v.id)
                          }));
                        }}
                        data-testid={`${userForm.role}-venue-checkbox-${v.id}`}
                      />
                      {v.name}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {userForm.role === 'cluster_manager'
                    ? 'This cluster manager will only be able to access the selected centre(s) across the system.'
                    : 'This landlord will only be able to view bookings for the selected building(s).'}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeUserModal} className={cardBorder}>
              Cancel
            </Button>
            <Button 
              onClick={editMode === 'user' ? handleUpdateUser : handleAddUser} 
              disabled={savingUser} 
              className="bg-primary hover:bg-primary/90"
            >
              {savingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (editMode === 'user' ? <Edit className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />)}
              {editMode === 'user' ? 'Update User' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Credits Modal */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className={`max-w-lg max-h-[85vh] overflow-y-auto ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Allocate Credits</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className={`p-4 rounded-lg ${innerCardBg} border ${cardBorder}`}>
              <p className="text-sm text-muted-foreground mb-1">Allocating to:</p>
              <p className="font-medium">
                {selectedOrgForCredit 
                  ? `Organization: ${organizations.find(o => o.id === selectedOrgForCredit)?.name || 'Unknown'}`
                  : selectedUserForCredit
                    ? `User: ${users.find(u => u.id === selectedUserForCredit)?.name || 'Unknown'}`
                    : 'Not selected'
                }
              </p>
            </div>

            {/* Section 1: Monthly Credits */}
            <div className={`p-4 rounded-xl border border-primary/20 ${isDark ? 'bg-primary/5' : 'bg-primary/5'} space-y-3`}>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-primary" />
                <Label className="text-sm font-semibold">Monthly Credits (Auto-Renewal)</Label>
              </div>
              <p className="text-xs text-muted-foreground">Credits auto-added on the 1st of every month</p>
              <div className="space-y-2">
                <Label className="text-xs">Credits per Month</Label>
                <Input type="number" min={0} placeholder="e.g., 50"
                  value={creditForm.monthly_credits || ''}
                  onChange={(e) => setCreditForm({...creditForm, monthly_credits: parseFloat(e.target.value) || 0})}
                  className={inputBg} data-testid="admin-monthly-credits-input" />
              </div>
              {creditForm.monthly_credits > 0 && (
                <>
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <div>
                      <Label className="text-xs">Auto-renew on 1st of every cycle</Label>
                      <p className="text-[11px] text-muted-foreground">If off, allocation is one-shot.</p>
                    </div>
                    <Switch
                      checked={creditForm.auto_renew !== undefined ? !!creditForm.auto_renew : true}
                      onCheckedChange={(v) => setCreditForm({...creditForm, auto_renew: v})}
                      data-testid="admin-credit-auto-renew-toggle"
                    />
                  </div>
                  {(creditForm.auto_renew !== undefined ? creditForm.auto_renew : true) && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Carry forward (%)</Label>
                        <Input type="number" min={0} max={100} step="1" placeholder="0"
                          value={creditForm.carry_forward_percentage ?? ''}
                          onChange={(e) => setCreditForm({...creditForm, carry_forward_percentage: parseFloat(e.target.value) || 0})}
                          className={inputBg} data-testid="admin-carry-forward-input" />
                        <p className="text-[10px] text-muted-foreground">% of unused balance kept on renewal</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Cycle (months)</Label>
                        <Input type="number" min={1} max={12} step="1" placeholder="1"
                          value={creditForm.renewal_cycle_months ?? 1}
                          onChange={(e) => setCreditForm({...creditForm, renewal_cycle_months: parseInt(e.target.value) || 1})}
                          className={inputBg} data-testid="admin-cycle-input" />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs">Programme End (optional)</Label>
                    <Input type="month" value={creditForm.monthly_valid_till || ''} onChange={(e) => setCreditForm({...creditForm, monthly_valid_till: e.target.value})}
                      className={inputBg} data-testid="admin-monthly-valid-till" />
                    <p className="text-xs text-muted-foreground">Auto-renewal stops after this month</p>
                  </div>
                </>
              )}
            </div>

            {/* Section 2: Additional Credit Package */}
            <div className={`p-4 rounded-xl border border-amber-500/20 ${isDark ? 'bg-amber-500/5' : 'bg-amber-50'} space-y-3`}>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-600" />
                <Label className="text-sm font-semibold">Additional Credit Package</Label>
              </div>
              <p className="text-xs text-muted-foreground">One-time top-up — never expires, never auto-renews. Used after monthly credits run out.</p>
              <div className="space-y-2">
                <Label className="text-xs">Credits (One-Time)</Label>
                <Input type="number" min={0} placeholder="e.g., 200"
                  value={creditForm.additional_credits || ''}
                  onChange={(e) => setCreditForm({...creditForm, additional_credits: parseFloat(e.target.value) || 0})}
                  className={inputBg} data-testid="admin-additional-credits-input" />
              </div>
            </div>

            {/* Summary */}
            {(creditForm.monthly_credits > 0 || creditForm.additional_credits > 0) && (
              <div className={`p-3 rounded-lg ${innerCardBg} border ${cardBorder} text-xs space-y-1`}>
                {creditForm.monthly_credits > 0 && (
                  <p><span className="font-medium">Monthly:</span> {creditForm.monthly_credits} credits/month (auto-renewal){creditForm.monthly_valid_till ? `, valid till ${creditForm.monthly_valid_till}` : ', no expiry'}</p>
                )}
                {creditForm.additional_credits > 0 && (
                  <p><span className="font-medium">Top-up:</span> {creditForm.additional_credits} credits (one-time, never expires)</p>
                )}
                <p className="font-medium mt-1">Total credits to add now: {(creditForm.monthly_credits || 0) + (creditForm.additional_credits || 0)}</p>
              </div>
            )}
            
            {/* Custom Credit Rates Section - Only for Organizations */}
            {selectedOrgForCredit && (
              <div className={`p-4 rounded-xl border ${cardBorder} ${innerCardBg}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-base">Custom Credit Rates</Label>
                    <p className="text-xs text-muted-foreground">Override default region rates for this organization</p>
                  </div>
                  <Switch 
                    checked={creditForm.custom_rates.enabled}
                    onCheckedChange={(checked) => setCreditForm({
                      ...creditForm, 
                      custom_rates: {...creditForm.custom_rates, enabled: checked}
                    })}
                  />
                </div>
                
                {creditForm.custom_rates.enabled && (
                  <div className="space-y-3 mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Credits per seat per hour by region:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Central Bangalore</Label>
                        <Input type="number" step="0.5" min={0} value={creditForm.custom_rates.central_bangalore}
                          onChange={(e) => setCreditForm({...creditForm, custom_rates: {...creditForm.custom_rates, central_bangalore: parseFloat(e.target.value) || 0}})}
                          className={`${inputBg} h-8 text-sm`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Koramangala</Label>
                        <Input type="number" step="0.5" min={0} value={creditForm.custom_rates.koramangala}
                          onChange={(e) => setCreditForm({...creditForm, custom_rates: {...creditForm.custom_rates, koramangala: parseFloat(e.target.value) || 0}})}
                          className={`${inputBg} h-8 text-sm`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">South Bangalore</Label>
                        <Input type="number" step="0.5" min={0} value={creditForm.custom_rates.south_bangalore}
                          onChange={(e) => setCreditForm({...creditForm, custom_rates: {...creditForm.custom_rates, south_bangalore: parseFloat(e.target.value) || 0}})}
                          className={`${inputBg} h-8 text-sm`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Electronic City</Label>
                        <Input type="number" step="0.5" min={0} value={creditForm.custom_rates.electronic_city}
                          onChange={(e) => setCreditForm({...creditForm, custom_rates: {...creditForm.custom_rates, electronic_city: parseFloat(e.target.value) || 0}})}
                          className={`${inputBg} h-8 text-sm`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">East Bangalore</Label>
                        <Input type="number" step="0.5" min={0} value={creditForm.custom_rates.east_bangalore}
                          onChange={(e) => setCreditForm({...creditForm, custom_rates: {...creditForm.custom_rates, east_bangalore: parseFloat(e.target.value) || 0}})}
                          className={`${inputBg} h-8 text-sm`} />
                      </div>
                    </div>
                    <p className="text-xs text-green-500 mt-2">
                      Custom rates will be applied when users from this organization make bookings
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreditModal(false); resetCreditForm(); }} className={cardBorder}>
              Cancel
            </Button>
            <Button onClick={handleAllocateCredits} disabled={savingCredit} className="bg-primary hover:bg-primary/90" data-testid="admin-allocate-credits-submit">
              {savingCredit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" />}
              Allocate Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, item: null })}>
        <AlertDialogContent className={isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {deleteDialog.type}
              {deleteDialog.item?.name ? ` "${deleteDialog.item.name}"` : ''}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={cardBorder}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
